import { useCustom, useTranslate } from "@refinedev/core";
import { Plus } from "lucide-react";
import { Button, Tile } from "@fawkes/ui";

import { CrematieDossierDetail } from "./CrematieDossierDetail";

// Het tabblad Uitvaart op het klantscherm (mockup): het dossier hangt onder de klant, niet op een
// eigen pagina. Heeft de klant meer uitvaarten, dan staat er een rij knoppen om er een te kiezen —
// geen tweede paginakop, geen "terug naar de klant", je bent al bij de klant.

interface KlantDossier {
  id: string;
  dossierNummer: string | null;
  dierNaam: string | null;
  status: string | null;
  aanmeldDatum: string | null;
}

export function CrematieUitvaartTab({
  relatieId,
  dossierId,
  onKies,
  onNieuweCrematie,
}: {
  relatieId: string;
  /** Het gekozen dossier; leeg = de nieuwste van deze klant. */
  dossierId: string | null;
  onKies: (dossierId: string) => void;
  onNieuweCrematie?: () => void;
}) {
  const t = useTranslate();
  const { data, isLoading, isError } = useCustom<KlantDossier[]>({
    url: `crematorium/relaties/${relatieId}/dossiers`,
    method: "get",
  });
  const rijen = Array.isArray(data?.data) ? data.data : [];
  const gekozen = dossierId && rijen.some((r) => r.id === dossierId) ? dossierId : rijen[0]?.id ?? null;

  if (isLoading) {
    return <p className="fawkes-muted">{t("common.loading")}</p>;
  }
  if (isError) {
    return <p className="fawkes-error">{t("crematiesTile.error")}</p>;
  }
  if (rijen.length === 0 || !gekozen) {
    return (
      <Tile title={t("relatieDetailView.tab.uitvaart")} className="fui-tile--breed">
        <p className="fawkes-tile-placeholder">{t("crematieUitvaartTab.geen")}</p>
        {onNieuweCrematie && (
          <Button variant="primary" onClick={onNieuweCrematie}>
            <Plus size={16} aria-hidden="true" /> {t("crematiesTile.nieuw")}
          </Button>
        )}
      </Tile>
    );
  }

  return (
    <div className="crem-uitvaarttab">
      {rijen.length > 1 && (
        <div className="crem-uitvaartkeuze" role="tablist" aria-label={t("relatieDetailView.tab.uitvaart")}>
          {rijen.map((r) => (
            <button
              key={r.id}
              type="button"
              role="tab"
              aria-selected={r.id === gekozen}
              className={"fawkes-relatie-onderdeel" + (r.id === gekozen ? " is-on" : "")}
              onClick={() => onKies(r.id)}
            >
              {r.dierNaam ?? t("crematoriumWerklijst.dier.onbekend")}
              <span className="fui-muted"> · {r.dossierNummer ?? "—"}</span>
            </button>
          ))}
        </div>
      )}
      <CrematieDossierDetail key={gekozen} id={gekozen} ingebed />
    </div>
  );
}
